import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import { buildFilesSigningParams, getGuaranteesSignedDocumentsFormData } from '../utils.ts';
import type {
  TFilesSigningDiiaApplyRequest,
  TFilesSigningDiiaInitializeRequest,
  TFilesSigningDiiaInitializeResponse,
  TFilesSigningDiiaStatusRequest,
  TFilesSigningDiiaStatusResponse,
  TFilesSigningGetHashesRequest,
  TFilesSigningGetHashesResponse,
  TFilesSigningSaveSignedDocumentsRawResponse,
  TFilesSigningSaveSignedDocumentsRequest,
  TFilesSigningSaveSignedDocumentsResponse,
} from '../types.ts';

export const getFilesHashesToSign = createAsyncThunk<
  TFilesSigningGetHashesResponse,
  TFilesSigningGetHashesRequest
>('guaranteesPage/filesSigning/getHashesToSign', async ({ fileIds }, { rejectWithValue }) => {
  try {
    const { data } = await api.post<TFilesSigningGetHashesResponse>(
      '/guarantee-api/files/get-hashes-to-sign',
      null,
      {
        params: buildFilesSigningParams({ fileIds }),
      }
    );

    return {
      documentsToSign: data.documentsToSign || [],
    };
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const initializeFilesDiiaSign = createAsyncThunk<
  TFilesSigningDiiaInitializeResponse,
  TFilesSigningDiiaInitializeRequest
>(
  'guaranteesPage/filesSigning/diiaInitialize',
  async ({ fileIds, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TFilesSigningDiiaInitializeResponse>(
        `/guarantee-api/files/application/${encodeURIComponent(applicationId)}/diia-sign/initialize`,
        null,
        {
          params: buildFilesSigningParams({ fileIds }),
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const getFilesDiiaSignStatus = createAsyncThunk<
  TFilesSigningDiiaStatusResponse,
  TFilesSigningDiiaStatusRequest
>(
  'guaranteesPage/filesSigning/diiaGetStatus',
  async ({ applicationId, requestId }, { rejectWithValue, signal }) => {
    try {
      const { data } = await api.get<TFilesSigningDiiaStatusResponse>(
        `/guarantee-api/files/applications/${encodeURIComponent(applicationId)}/diia-sign/get-status/${encodeURIComponent(requestId)}`,
        { signal }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const applyFilesDiiaSign = createAsyncThunk<
  TFilesSigningDiiaStatusResponse,
  TFilesSigningDiiaApplyRequest
>('guaranteesPage/filesSigning/diiaApply', async ({ applicationId, requestId }, { rejectWithValue }) => {
  try {
    const { data } = await api.post<TFilesSigningDiiaStatusResponse>(
      `/guarantee-api/files/applications/${encodeURIComponent(applicationId)}/diia-sign/apply/${encodeURIComponent(requestId)}`,
      null
    );

    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const saveFilesSignedDocuments = createAsyncThunk<
  TFilesSigningSaveSignedDocumentsResponse,
  TFilesSigningSaveSignedDocumentsRequest
>(
  'guaranteesPage/filesSigning/saveSignedDocuments',
  async ({ fileList, applicationId }, { rejectWithValue }) => {
    try {
      const formData = getGuaranteesSignedDocumentsFormData(fileList);
      const { data } = await api.post<TFilesSigningSaveSignedDocumentsRawResponse>(
        `/guarantee-api/files/applications/${encodeURIComponent(applicationId)}/save-signed-documents`,
        formData
      );

      if (Array.isArray(data)) {
        return {
          unSavedDocuments: [],
          uploadedDocuments: data.map((document) => ({
            fileExtension: '',
            fileName: document.fileName,
            id: document.id,
            status: 200,
          })),
        };
      }

      return {
        documentsToSign: data?.documentsToSign,
        unSavedDocuments: data?.unSavedDocuments || [],
        uploadedDocuments: data?.uploadedDocuments || [],
        version: data?.version,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
